import React, { useEffect, useState } from "react";
import { ReactQuestionFactory } from "survey-react-ui";
import { Question, Serializer, ElementFactory } from "survey-core";

export class MonthYearPickerQuestionModel extends Question {
  static readonly type = "monthyearpicker";

  constructor(name: string) {
    super(name);
  }

  getType() {
    return MonthYearPickerQuestionModel.type;
  }

  public getDefaultValue(): unknown {
    return undefined;
  }

  public isValueEmpty(value: unknown): boolean {
    return value === undefined || value === null || value === "";
  }
}

interface MonthYearPickerQuestionComponentProps {
  question: MonthYearPickerQuestionModel;
}

const VALUE_CHANGE_KEY = "monthyearpicker-value";

const extractYearMonth = (raw: unknown): string | null => {
  if (typeof raw !== "string" || raw.length === 0) return null;
  const match = /^(\d{4})-(\d{2})/.exec(raw);
  return match ? `${match[1]}-${match[2]}` : null;
};

export function MonthYearPickerQuestionComponent({
  question,
}: MonthYearPickerQuestionComponentProps) {
  const [value, setValue] = useState<string | null>(() =>
    extractYearMonth(question.value),
  );

  useEffect(() => {
    const handler = () => {
      setValue(extractYearMonth(question.value));
    };
    question.registerFunctionOnPropertyValueChanged(
      "value",
      handler,
      VALUE_CHANGE_KEY,
    );
    return () => {
      question.unRegisterFunctionOnPropertyValueChanged(
        "value",
        VALUE_CHANGE_KEY,
      );
    };
  }, [question]);

  const isReadOnly = question.isReadOnly;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    if (!next) {
      setValue(null);
      question.clearValue();
      return;
    }
    setValue(next);
    question.value = next;
  };

  return (
    <div className={question.cssClasses?.root}>
      <input
        type="month"
        value={value ?? ""}
        onChange={handleChange}
        disabled={isReadOnly}
        max={new Date().toISOString().slice(0, 7)}
      />
    </div>
  );
}

export function registerMonthYearPickerQuestion() {
  ElementFactory.Instance.registerElement(
    "monthyearpicker",
    (name: string) => new MonthYearPickerQuestionModel(name),
  );

  Serializer.addClass(
    "monthyearpicker",
    [],
    () => new MonthYearPickerQuestionModel(""),
    "question",
  );

  ReactQuestionFactory.Instance.registerQuestion(
    MonthYearPickerQuestionModel.type,
    (propsOrName: string | MonthYearPickerQuestionComponentProps) => {
      const props =
        typeof propsOrName === "string"
          ? { question: new MonthYearPickerQuestionModel(propsOrName) }
          : propsOrName;
      return React.createElement(MonthYearPickerQuestionComponent, props);
    },
  );
}
